import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, X, Download, Users, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import { createUsersFromExcel, validateExcelUsersData } from '../../Fetch/fetchUser';
import { getListApartmensByBuildingId } from '../../Fetch/fetchapartment';
import styles from './ExcelUpload.module.css';
import * as XLSX from 'xlsx';

const ExcelUpload = ({ onClose, onUsersCreated, buildingId }) => {
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]); // Store all raw data
  const [excelData, setExcelData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0); // New state for header row selection
  const [columnMapping, setColumnMapping] = useState({
    tenantName: '',
    tenantPhone: '',
    email: '',
    apartmentNumber: '',
    floorNumber: '',
    relationshipType: '',
    ownerName: '',
    ownerPhone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ success: [], errors: [] });
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Header Selection, 3: Mapping, 4: Processing, 5: Results
  const [apartments, setApartments] = useState([]); // Store apartment data for ID lookup

  // Fetch apartments when component mounts
  useEffect(() => {
    const fetchApartments = async () => {
      try {
        console.log('🔄 Fetching apartments for building:', buildingId);
        const apartmentData = await getListApartmensByBuildingId(buildingId);
        console.log('✅ Fetched apartments data:', apartmentData);
        console.log('📊 Apartments count:', apartmentData?.length || 0);
        
        if (apartmentData && apartmentData.length > 0) {
          console.log('🏠 First apartment structure:', apartmentData[0]);
          console.log('🏠 Available apartment numbers:', 
            apartmentData.map(apt => apt.apartmentNumber || apt.ApartmentNumber || 'NO_NUMBER')
          );
        }
        
        setApartments(apartmentData || []);
      } catch (error) {
        console.error('❌ Failed to fetch apartments:', error);
        setApartments([]);
      }
    };

    if (buildingId) {
      fetchApartments();
    } else {
      console.log('⚠️ No buildingId provided to ExcelUpload');
    }
  }, [buildingId]);

  // Helper function to get apartment ID from apartment number
  const getApartmentIdByNumber = (apartmentNumber) => {
    console.log('🔍 Looking for apartment number:', apartmentNumber);
    console.log('🏠 Available apartments count:', apartments.length);
    
    if (apartments.length > 0 && apartments.length <= 5) {
      console.log('🏠 Available apartments (first 5):', apartments.map(apt => ({
        id: apt.apartmentID || apt.ApartmentID || apt.id || apt.ID,
        number: apt.apartmentNumber || apt.ApartmentNumber || apt.number || apt.Number,
        allProps: Object.keys(apt)
      })));
    } else if (apartments.length > 5) {
      console.log('🏠 Available apartments (sample of 5):', apartments.slice(0, 5).map(apt => ({
        id: apt.apartmentID || apt.ApartmentID || apt.id || apt.ID,
        number: apt.apartmentNumber || apt.ApartmentNumber || apt.number || apt.Number,
        allProps: Object.keys(apt)
      })));
    }

    if (!apartmentNumber || apartments.length === 0) {
      console.log('❌ No apartment number provided or no apartments loaded');
      return null;
    }

    const searchNum = String(apartmentNumber).trim();
    console.log(`🔍 Searching for apartment number: "${searchNum}"`);

    const apartment = apartments.find(apt => {
      // Check all possible property names for apartment number
      const aptNum1 = apt.apartmentNumber;
      const aptNum2 = apt.ApartmentNumber;
      const aptNum3 = apt.number;
      const aptNum4 = apt.Number;
      
      const match1 = aptNum1 && String(aptNum1).trim() === searchNum;
      const match2 = aptNum2 && String(aptNum2).trim() === searchNum;
      const match3 = aptNum3 && String(aptNum3).trim() === searchNum;
      const match4 = aptNum4 && String(aptNum4).trim() === searchNum;
      
      const hasMatch = match1 || match2 || match3 || match4;
      
      if (hasMatch) {
        console.log('✅ Found match:', {
          searchNum,
          aptNum1, match1,
          aptNum2, match2,
          aptNum3, match3,
          aptNum4, match4,
          apartmentData: apt
        });
      }
      
      return hasMatch;
    });

    if (!apartment) {
      console.log('❌ No apartment found for number:', searchNum);
      return null;
    }

    // Get the apartment ID, checking all possible property names
    const apartmentId = apartment.apartmentID || apartment.ApartmentID || apartment.id || apartment.ID;
    console.log('✅ Found apartment ID:', apartmentId, 'for apartment:', apartment);
    
    return apartmentId;
  };

  // Update headers and data when header row selection changes
  const updateHeadersAndData = useCallback((data, headerIndex) => {
    if (data.length <= headerIndex) return;

    const headerRow = data[headerIndex].map(header =>
      header ? String(header).trim() : ''
    );

    const dataRows = data.slice(headerIndex + 1)
      .filter(row => row && row.some(cell => cell !== undefined && cell !== null && cell !== ''))
      .map(row => row.map(cell =>
        cell !== undefined && cell !== null ? String(cell).trim() : ''
      ));

    setHeaders(headerRow);
    setExcelData(dataRows);
  }, []);

  // Handle header row selection change
  const handleHeaderRowChange = (newHeaderIndex) => {
    setHeaderRowIndex(newHeaderIndex);
    updateHeadersAndData(rawData, newHeaderIndex);
    // Reset column mapping when header row changes
    setColumnMapping({
      tenantName: '',
      tenantPhone: '',
      email: '',
      apartmentNumber: '',
      floorNumber: '',
      relationshipType: '',
      ownerName: '',
      ownerPhone: ''
    });
  };

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.match(/\.(xlsx|xls|csv)$/)) {
      alert('אנא בחר קובץ Excel (.xlsx, .xls) או CSV (.csv)');
      return;
    }

    setFile(uploadedFile);

    try {
      let processedData = [];

      if (uploadedFile.name.match(/\.csv$/)) {
        // Handle CSV files
        const text = await uploadedFile.text();
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
          return;
        }

        processedData = lines.map(line =>
          line.split(',').map(cell => cell.trim().replace(/"/g, ''))
        ).filter(row => row.some(cell => cell !== ''));

      } else if (uploadedFile.name.match(/\.xlsx?$/)) {
        // Handle Excel files using xlsx library
        try {
          const arrayBuffer = await uploadedFile.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
            return;
          }

          processedData = jsonData;

        } catch (error) {
          alert('שגיאה בקריאת קובץ ה-Excel. אנא בדוק שהקובץ תקין');
          return;
        }
      } else {
        alert('נא להעלות קובץ CSV או Excel בלבד');
        return;
      }

      // Store raw data and initialize with first row as header
      setRawData(processedData);
      setHeaderRowIndex(0);
      updateHeadersAndData(processedData, 0);
      setCurrentStep(2); // Go to header selection step

    } catch (error) {
      alert('שגיאה בקריאת הקובץ');
    }
  }, [updateHeadersAndData]);

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Validate phone number
  const isValidPhone = (phone) => {
    if (!phone || phone.trim() === '') return true; // Allow empty phone
    // Israeli phone validation - accepts various formats
    const phoneRegex = /^[\d\-\s\+\(\)]{9,15}$/;
    return phoneRegex.test(phone.trim());
  };



  // Generate more unique temporary email
  const generateUniqueEmail = (index) => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `temp_${timestamp}_${index}_${random}@noemail.local`;
  };

  // Generate more unique temporary phone
  const generateUniquePhone = (index) => {
    const timestamp = Date.now().toString().slice(-6);
    return `000${timestamp.slice(0, 3)}${timestamp.slice(3)}`;
  };


  // processUsers function - modified to include phone validation
  const processUsers = async () => {
    // Require at least tenant name or apartment number
    if (!columnMapping.tenantName && !columnMapping.apartmentNumber) {
      alert('חובה לבחור עמודה עבור שם דייר או מספר דירה');
      return;
    }

    setIsProcessing(true);
    setCurrentStep(4); // Processing step
    setProgress({ current: 0, total: excelData.length });

    try {
      // Prepare data for bulk creation
      const usersToCreate = [];

      // Map Excel data to user objects
      const tenantNameIndex = columnMapping.tenantName ?
        headers.findIndex(h => h === columnMapping.tenantName) : -1;
      const tenantPhoneIndex = columnMapping.tenantPhone ?
        headers.findIndex(h => h === columnMapping.tenantPhone) : -1;
      const emailIndex = columnMapping.email ?
        headers.findIndex(h => h === columnMapping.email) : -1;
      const apartmentIndex = columnMapping.apartmentNumber ?
        headers.findIndex(h => h === columnMapping.apartmentNumber) : -1;
      const floorIndex = columnMapping.floorNumber ?
        headers.findIndex(h => h === columnMapping.floorNumber) : -1;
      const relationshipIndex = columnMapping.relationshipType ?
        headers.findIndex(h => h === columnMapping.relationshipType) : -1;
      const ownerNameIndex = columnMapping.ownerName ?
        headers.findIndex(h => h === columnMapping.ownerName) : -1;
      const ownerPhoneIndex = columnMapping.ownerPhone ?
        headers.findIndex(h => h === columnMapping.ownerPhone) : -1;

      // Process each row to create user data objects
      for (let i = 0; i < excelData.length; i++) {
        const row = excelData[i];
        
        // Update progress for every 5 rows to make it smoother
        if (i % 5 === 0 || i === excelData.length - 1) {
          setProgress({ current: i + 1, total: excelData.length });
          // Allow UI to update
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        const tenantName = tenantNameIndex >= 0 ? row[tenantNameIndex]?.toString().trim() : '';
        const tenantPhone = tenantPhoneIndex >= 0 ? row[tenantPhoneIndex]?.toString().trim() : '';
        const email = emailIndex >= 0 ? row[emailIndex]?.toString().trim() : '';
        const apartmentNumber = apartmentIndex >= 0 ? row[apartmentIndex]?.toString().trim() : null;
        const floorNumber = floorIndex >= 0 ?
          (row[floorIndex] ? parseInt(row[floorIndex]) : 0) : 0;
        const relationshipType = relationshipIndex >= 0 ? row[relationshipIndex]?.toString().trim() : '';
        const ownerName = ownerNameIndex >= 0 ? row[ownerNameIndex]?.toString().trim() : '';
        const ownerPhone = ownerPhoneIndex >= 0 ? row[ownerPhoneIndex]?.toString().trim() : '';

        // Get apartment ID if apartment number is provided
        const apartmentId = apartmentNumber ? getApartmentIdByNumber(apartmentNumber) : null;

        console.log(`🔍 Row ${i + 1}: apartmentNumber="${apartmentNumber}", apartmentId=${apartmentId}`);

        const userData = {
          FullName: tenantName || `דייר דירה ${apartmentNumber}` || 'דייר',
          Email: email || '',
          Phone: tenantPhone || '', // This creates the user account phone
          PasswordHash: generatePassword(),
          IsManager: false,
          IsVaad: false,
          IsTenant: true,
          BuildingId: buildingId,
          // Only include apartment data if we have a valid apartment ID
          ...(apartmentNumber && { ApartmentNumber: apartmentNumber }),
          ...(apartmentId !== null && apartmentId !== undefined && { ApartmentID: apartmentId }),
          RelationshipType: relationshipType || 'בעלים',
          OwnerName: ownerName || '',
          OwnerPhone: ownerPhone || '',
          TenantName: tenantName || '', // This populates the apartment tenant field
          TenantPhone: tenantPhone || '', // This populates the apartment tenant phone field
          ...(floorNumber !== undefined && { FloorNumber: floorNumber }),
          VaadMonthly: 0,
          // Add row index for error tracking
          _rowIndex: i,
          _originalRowNumber: i + headerRowIndex + 2
        };

        // Log the final userData to see what's being sent
        console.log(`📤 Row ${i + 1} final data:`, {
          FullName: userData.FullName,
          ApartmentNumber: userData.ApartmentNumber,
          ApartmentID: userData.ApartmentID,
          hasApartmentID: 'ApartmentID' in userData
        });

        usersToCreate.push(userData);
      }

      console.log('Users to create:', usersToCreate);

      // Validate data before sending
      const validation = validateExcelUsersData(usersToCreate);
      if (!validation.isValid) {
        console.error('Validation errors:', validation.errors);
        alert('שגיאות בנתונים:\n' + validation.errors.join('\n'));
        setIsProcessing(false);
        return;
      }

      // Show that we're processing the data
      setProgress({ current: excelData.length, total: excelData.length + 1 }); // Show almost complete
      
      // Add small delay to show progress before API call
      await new Promise(resolve => setTimeout(resolve, 100));

      // Call the bulk API
      const result = await createUsersFromExcel(usersToCreate);

      console.log('Bulk creation result:', result);
      
      // Debug apartment 13 specifically
      const apt13User = usersToCreate.find(u => u.ApartmentNumber === '13');
      if (apt13User) {
        console.log('🏠 APARTMENT 13 was sent to API:', apt13User);
        
        // Check if apartment 13 appears in success results
        const apt13Success = result.successes?.find(s => s.ApartmentNumber === '13' || s.FullName === apt13User.FullName);
        console.log('🏠 APARTMENT 13 in success results:', apt13Success);
        
        // Check if apartment 13 appears in error results  
        const apt13Error = result.errors?.find(e => e.userIndex === (apt13User._rowIndex + 1) || e.fullName === apt13User.FullName);
        console.log('🏠 APARTMENT 13 in error results:', apt13Error);
      }

      // Update progress to show completion
      setProgress({ current: excelData.length + 1, total: excelData.length + 1 });

      // Process the results from the bulk API
      const successResults = [];
      const errorResults = [];

      // Handle success results
      if (result.successes && Array.isArray(result.successes)) {
        result.successes.forEach(success => {
          const originalUser = usersToCreate[success.userIndex - 1];
          successResults.push({
            row: originalUser?._originalRowNumber || success.userIndex,
            fullName: success.FullName,
            email: success.Email === 'נוצר זמני' ? 'נוצר זמני' : success.Email,
            phone: success.Phone === 'נוצר זמני' ? 'נוצר זמני' : success.Phone,
            apartmentNumber: success.ApartmentNumber || 'לא צוין',
            floorNumber: success.FloorNumber || 0,
            relationshipType: success.RelationshipType || 'בעלים',
            ownerName: success.OwnerName || 'לא צוין',
            ownerPhone: success.OwnerPhone || 'לא צוין',
            message: 'נוצר בהצלחה',
            passwordGenerated: success.PasswordGenerated,
            temporaryPassword: success.TemporaryPassword
          });
        });
      }

      // Handle error results
      if (result.errors && Array.isArray(result.errors)) {
        result.errors.forEach(error => {
          errorResults.push({
            row: error.userIndex,
            fullName: error.fullName || 'לא ידוע',
            email: error.email || 'לא צוין',
            phone: error.phone || 'לא צוין',
            error: error.error
          });
        });
      }

      // If the API returned a generic error but some users were processed
      if (!result.successes && !result.errors && result.successCount !== undefined) {
        // Handle case where detailed results aren't available
        for (let i = 0; i < result.successCount; i++) {
          const user = usersToCreate[i];
          successResults.push({
            row: user._originalRowNumber,
            fullName: user.FullName,
            email: user.Email || 'נוצר זמני',
            phone: user.Phone || 'נוצר זמני',
            apartmentNumber: user.ApartmentNumber || 'לא צוין',
            floorNumber: user.FloorNumber || 0,
            relationshipType: user.RelationshipType || 'בעלים',
            ownerName: user.OwnerName || 'לא צוין',
            ownerPhone: user.OwnerPhone || 'לא צוין',
            message: 'נוצר בהצלחה'
          });
        }
      }

      setResults({ success: successResults, errors: errorResults });
      setIsProcessing(false);
      setCurrentStep(5); // Results step

      // Notify parent component to refresh users list
      if (successResults.length > 0) {
        onUsersCreated();
      }

    } catch (error) {
      console.error('Bulk user creation error:', error);

      // Handle the error appropriately
      const errorMessage = error.message || 'שגיאה בלתי צפויה ביצירת המשתמשים';

      setResults({
        success: [],
        errors: [{
          row: 'כללי',
          fullName: 'שגיאה כללית',
          email: '',
          phone: '',
          error: errorMessage
        }]
      });

      setIsProcessing(false);
      setCurrentStep(5); // Results step

      alert(`שגיאה ביצירת המשתמשים: ${errorMessage}`);
    }
  };

  // Download template - updated to focus on tenant information
  const downloadTemplate = () => {
    const csvContent = [
      ['שם דייר', 'טלפון דייר', 'דוא"ל (אופציונלי)', 'מספר דירה', 'מספר קומה', 'סוג יחס', 'שם בעלים', 'טלפון בעלים'],
      ['יוסי כהן', '052-1234567', 'yossi@example.com', '12', '3', 'בעלים', '', ''],
      ['רחל לוי', '054-9876543', '', '15', '4', 'שוכר', 'יוסי כהן', '052-1111111'],
      ['דוד ישראלי', '', 'david@example.com', '8', '2', 'בעלים', '', '']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_users.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUpload = () => {
    setFile(null);
    setRawData([]);
    setExcelData([]);
    setHeaders([]);
    setHeaderRowIndex(0);
    setColumnMapping({ tenantName: '', tenantPhone: '', email: '', apartmentNumber: '', floorNumber: '', relationshipType: '', ownerName: '', ownerPhone: '' });
    setCurrentStep(1);
    setResults({ success: [], errors: [] });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            <FileSpreadsheet size={24} />
            העלאת דיירים מקובץ Excel או CSV
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Step 1: File Upload */}
        {currentStep === 1 && (
          <div className={styles.content}>
            <div className={styles.uploadSection}>
              <div className={styles.instructions}>
                <h3>הוראות:</h3>
                <ul>
                  <li>קובץ CSV או Excel חייב להכיל שורת כותרות</li>
                  <li>חובה: לפחות שם דייר או מספר דירה</li>
                  <li>אופציונלי: דוא"ל, מספר קומה, סוג יחס, שם בעלים, וטלפון בעלים</li>
                  <li>טלפון דייר: מספר בפורמט ישראלי (למשל: 052-1234567 או 0521234567)</li>
                  <li>סוג יחס: "בעלים" או "שוכר" (ברירת מחדל: בעלים)</li>
                  <li>שם בעלים וטלפון בעלים: רלוונטי רק כאשר סוג היחס הוא "שוכר"</li>
                  <li>כל המשתמשים יוגדרו כדיירים עם הרשאות בסיסיות</li>
                  <li>קומה ריקה תוגדר כ-0</li>
                  <li>אם אין דוא"ל, המערכת תיצור דוא"ל זמני</li>
                  <li>מומלץ: השתמש בקבצי CSV לתאימות מלאה</li>
                </ul>
              </div>

              <div className={styles.uploadArea}>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                  id="excel-upload"
                />
                <label htmlFor="excel-upload" className={styles.uploadLabel}>
                  <Upload size={48} />
                  <span>לחץ לבחירת קובץ CSV או Excel</span>
                  <small>קבצים נתמכים: .csv, .xlsx, .xls</small>
                </label>
              </div>

              <div className={styles.templateSection}>
                <button className={styles.templateButton} onClick={downloadTemplate}>
                  <Download size={16} />
                  הורד תבנית CSV לדוגמה
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Header Row Selection */}
        {currentStep === 2 && (
          <div className={styles.content}>
            <div className={styles.headerSelection}>
              <h3>בחר שורת כותרות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} | <strong>סה"כ שורות:</strong> {rawData.length}
              </div>

              <div className={styles.headerRowSelector}>
                <label>שורת כותרות (מתחילה מ-1):</label>
                <select
                  value={headerRowIndex + 1}
                  onChange={(e) => handleHeaderRowChange(parseInt(e.target.value) - 1)}
                  className={styles.columnSelect}
                >
                  {rawData.map((_, index) => (
                    <option key={index} value={index + 1}>
                      שורה {index + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview of selected header row and data */}
              <div className={styles.headerPreview}>
                <h4>תצוגה מקדימה של הקובץ:</h4>
                <div className={styles.previewContainer}>
                  <div className={styles.previewTable}>
                    <table>
                      <tbody>
                        {rawData.slice(0, Math.min(10, rawData.length)).map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex === headerRowIndex ? styles.headerRow : ''}>
                            <td className={styles.rowNumber}>
                              {rowIndex + 1}
                              {rowIndex === headerRowIndex && <span className={styles.headerIndicator}> (כותרות)</span>}
                            </td>
                            {row.slice(0, 8).map((cell, cellIndex) => (
                              <td key={cellIndex} className={rowIndex === headerRowIndex ? styles.headerCell : ''}>
                                {cell || '(ריק)'}
                              </td>
                            ))}
                            {row.length > 8 && <td>...</td>}
                          </tr>
                        ))}
                        {rawData.length > 10 && (
                          <tr>
                            <td colSpan="100%" style={{ textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                              ו-{rawData.length - 10} שורות נוספות...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {headers.length > 0 && (
                  <div className={styles.selectedHeaders}>
                    <h4>כותרות שנבחרו:</h4>
                    <div className={styles.headersList}>
                      {headers.map((header, index) => (
                        <span key={index} className={styles.headerTag}>
                          {header || `עמודה ${index + 1}`}
                        </span>
                      ))}
                    </div>
                    <p className={styles.dataInfo}>
                      <strong>שורות נתונים זמינות:</strong> {excelData.length}
                    </p>
                  </div>
                )}
              </div>

              <div className={styles.actions}>
                <button className={styles.backButton} onClick={resetUpload}>
                  חזור
                </button>
                <button
                  className={styles.processButton}
                  onClick={() => setCurrentStep(3)}
                  disabled={headers.length === 0 || excelData.length === 0}
                >
                  המשך להתאמת עמודות
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Column Mapping */}
        {currentStep === 3 && (
          <div className={styles.content}>
            <div className={styles.mappingSection}>
              <h3>בחר התאמת עמודות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} |
                <strong> שורת כותרות:</strong> {headerRowIndex + 1} |
                <strong> שורות נתונים:</strong> {excelData.length}
              </div>

              <div className={styles.mappingGrid}>
                <div className={styles.mappingRow}>
                  <label>שם דייר (מומלץ):</label>
                  <select
                    value={columnMapping.tenantName}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, tenantName: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>טלפון דייר (אופציונלי):</label>
                  <select
                    value={columnMapping.tenantPhone}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, tenantPhone: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>דוא"ל (אופציונלי):</label>
                  <select
                    value={columnMapping.email}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, email: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>


                <div className={styles.mappingRow}>
                  <label>מספר דירה (אופציונלי):</label>
                  <select
                    value={columnMapping.apartmentNumber}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, apartmentNumber: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>מספר קומה (אופציונלי):</label>
                  <select
                    value={columnMapping.floorNumber}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, floorNumber: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.mappingRow}>
                  <label>סוג יחס (אופציונלי):</label>
                  <select
                    value={columnMapping.relationshipType}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, relationshipType: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>שם בעלים (אופציונלי):</label>
                  <select
                    value={columnMapping.ownerName}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, ownerName: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>טלפון בעלים (אופציונלי):</label>
                  <select
                    value={columnMapping.ownerPhone}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, ownerPhone: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

              </div>

              {/* Preview */}
              <div className={styles.preview}>
                <h4>תצוגה מקדימה (3 שורות ראשונות):</h4>
                <div className={styles.previewTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>שם דייר</th>
                        <th>טלפון דייר</th>
                        <th>דוא"ל</th>
                        <th>מספר דירה</th>
                        <th>מספר קומה</th>
                        <th>סוג יחס</th>
                        <th>שם בעלים</th>
                        <th>טלפון בעלים</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.slice(0, 3).map((row, index) => {
                        const tenantNameIndex = columnMapping.tenantName ?
                          headers.findIndex(h => h === columnMapping.tenantName) : -1;
                        const tenantPhoneIndex = columnMapping.tenantPhone ?
                          headers.findIndex(h => h === columnMapping.tenantPhone) : -1;
                        const emailIndex = columnMapping.email ?
                          headers.findIndex(h => h === columnMapping.email) : -1;
                        const apartmentIndex = columnMapping.apartmentNumber ?
                          headers.findIndex(h => h === columnMapping.apartmentNumber) : -1;
                        const floorIndex = columnMapping.floorNumber ?
                          headers.findIndex(h => h === columnMapping.floorNumber) : -1;
                        const relationshipIndex = columnMapping.relationshipType ?
                          headers.findIndex(h => h === columnMapping.relationshipType) : -1;
                        const ownerNameIndex = columnMapping.ownerName ?
                          headers.findIndex(h => h === columnMapping.ownerName) : -1;
                        const ownerPhoneIndex = columnMapping.ownerPhone ?
                          headers.findIndex(h => h === columnMapping.ownerPhone) : -1;

                        return (
                          <tr key={index}>
                            <td>{tenantNameIndex >= 0 ? (row[tenantNameIndex] || 'לא צוין') : 'לא נבחר'}</td>
                            <td>{tenantPhoneIndex >= 0 ? (row[tenantPhoneIndex] || 'לא צוין') : 'לא נבחר'}</td>
                            <td>{emailIndex >= 0 ? (row[emailIndex] || 'לא צוין') : 'לא נבחר'}</td>
                            <td>{apartmentIndex >= 0 ? row[apartmentIndex] || 'ריק' : 'לא נבחר'}</td>
                            <td>{floorIndex >= 0 ? row[floorIndex] || '0' : 'לא נבחר'}</td>
                            <td>{relationshipIndex >= 0 ? row[relationshipIndex] || 'בעלים (ברירת מחדל)' : 'לא נבחר'}</td>
                            <td>{ownerNameIndex >= 0 ? (row[ownerNameIndex] || 'לא צוין') : 'לא נבחר'}</td>
                            <td>{ownerPhoneIndex >= 0 ? (row[ownerPhoneIndex] || 'לא צוין') : 'לא נבחר'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={() => setCurrentStep(2)}>
                חזור
              </button>
              <button
                className={styles.processButton}
                onClick={processUsers}
                disabled={!columnMapping.tenantName && !columnMapping.apartmentNumber}
              >
                <Users size={16} />
                יצירת {excelData.length} משתמשים
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Processing */}
        {currentStep === 4 && (
          <div className={styles.content}>
            <div className={styles.processing}>
              <div className={styles.loadingSpinner}>
                <Loader className={styles.spinnerIcon} size={48} />
              </div>
              <div className={styles.progressSection}>
                <h3>יוצר משתמשים...</h3>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
                <div className={styles.progressText}>
                  {progress.current === progress.total && progress.total > 0 
                    ? 'שולח לשרת...' 
                    : `מעבד ${progress.current} מתוך ${progress.total} שורות`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Results */}
        {currentStep === 5 && (
          <div className={styles.content}>
            <div className={styles.results}>
              <h3>תוצאות העלאה</h3>

              <div className={styles.resultsSummary}>
                <div className={`${styles.summaryCard} ${styles.success}`}>
                  <CheckCircle size={20} />
                  <span>נוצרו בהצלחה: {results.success.length}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.error}`}>
                  <XCircle size={20} />
                  <span>שגיאות: {results.errors.length}</span>
                </div>
              </div>

              {/* Always show success results section */}
              <div className={styles.successResults}>
                <h4>
                  <CheckCircle size={16} />
                  משתמשים שנוצרו בהצלחה ({results.success.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.success.length > 0 ? (
                    results.success.map((result, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>{result.fullName} ({result.email}) - {result.phone}</span>
                        <small>
                          {result.apartmentNumber !== 'לא צוין' && `דירה ${result.apartmentNumber}, `}
                          קומה {result.floorNumber}, {result.relationshipType}
                          {result.relationshipType === 'שוכר' && result.ownerName !== 'לא צוין' && (
                            <span>, בעלים: {result.ownerName} {result.ownerPhone !== 'לא צוין' && `(${result.ownerPhone})`}</span>
                          )}
                          <br />
                          <strong>סיסמה זמנית נוצרה עבור המשתמש</strong>
                        </small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#999', fontStyle: 'italic' }}>לא נוצרו משתמשים</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Always show errors section */}
              <div className={styles.errorResults}>
                <h4>
                  <AlertCircle size={16} />
                  שגיאות ({results.errors.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.errors.length > 0 ? (
                    results.errors.map((error, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>שורה {error.row}: {error.fullName} ({error.email}) - {error.phone}</span>
                        <small className={styles.errorMessage}>{error.error}</small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#28a745', fontStyle: 'italic' }}>אין שגיאות - כל המשתמשים נוצרו בהצלחה!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={resetUpload}>
                העלאה חדשה
              </button>
              <button className={styles.closeButton} onClick={onClose}>
                סגור
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelUpload;